import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { ArrowRight, Check, RotateCcw, ScanLine } from 'lucide-react';
import LensWorkflowDemo from './LensWorkflowDemo';

const getTodayKey = (appId) => {
  const d = new Date();
  return `workflow_${appId}_${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const WorkflowTracker = ({ appId, label, steps, accentColor = 'bg-black' }) => {
  const navigate = useNavigate();
  const storageKey = getTodayKey(appId);
  const [lensOpen, setLensOpen] = useState(false);
  const [lensContext, setLensContext] = useState(null);

  const [completed, setCompleted] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(completed));
  }, [completed, storageKey]);

  const toggleStep = useCallback((num, e) => {
    e.stopPropagation();
    setCompleted(prev => {
      const next = { ...prev };
      next[num] ? delete next[num] : (next[num] = true);
      return next;
    });
  }, []);

  const resetAll = useCallback(() => setCompleted({}), []);

  const openLensDemo = useCallback((ctx, e) => {
    e.stopPropagation();
    setLensContext(ctx);
    setLensOpen(true);
  }, []);

  const completedCount = Object.keys(completed).length;
  const total = steps.length;
  const pct = Math.round((completedCount / total) * 100);

  return (
    <div data-testid={`${appId}-workflow-section`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
          <Badge variant="outline" className={cn("text-[10px] gap-1 px-2 py-0.5 font-semibold", pct === 100 ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200")} data-testid={`${appId}-workflow-progress`}>
            {pct === 100 ? <Check className="h-3 w-3" /> : null}
            {completedCount}/{total} &middot; {pct}%
          </Badge>
        </div>
        {completedCount > 0 && (
          <button onClick={resetAll} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-slate-600 transition-colors" data-testid={`${appId}-workflow-reset`}>
            <RotateCcw className="h-3 w-3" /> Reset
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-slate-100 rounded-full mb-3 overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-500", pct === 100 ? "bg-emerald-500" : accentColor)} style={{ width: `${pct}%` }} data-testid={`${appId}-workflow-bar`} />
      </div>

      <div className={`grid gap-2 bg-white border rounded-xl p-3`} style={{ gridTemplateColumns: `repeat(${total}, 1fr)` }}>
        {steps.map((step, i) => {
          const done = !!completed[step.num];
          return (
            <div key={step.num} className="flex items-start gap-0">
              <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors w-full group">
                <button
                  onClick={(e) => toggleStep(step.num, e)}
                  className={cn(
                    "h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 transition-all duration-300 cursor-pointer",
                    done ? "bg-emerald-500 text-white scale-110" : `${accentColor} text-white hover:opacity-80`
                  )}
                  data-testid={`${appId}-step-check-${step.num}`}
                  title={done ? 'Mark incomplete' : 'Mark complete'}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : step.num}
                </button>
                <div className="text-left min-w-0">
                  <button onClick={() => step.lens ? openLensDemo(step.lens, { stopPropagation: () => {} }) : navigate(step.href)} className="text-left" data-testid={`${appId}-step-${step.num}`}>
                    <span className={cn("text-xs font-semibold block transition-colors", done ? "text-emerald-700 line-through decoration-emerald-300" : "text-slate-800")}>{step.title}</span>
                    <span className={cn("text-[10px] block leading-snug transition-colors", done ? "text-emerald-500" : "text-slate-400")}>{step.desc}</span>
                  </button>
                  {step.lens && (
                    <button onClick={(e) => openLensDemo(step.lens, e)} className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-violet-50 border border-violet-200 text-[9px] font-medium text-violet-700 hover:bg-violet-100 transition-colors" data-testid={`${appId}-lens-${step.num}`}>
                      <ScanLine className="h-2.5 w-2.5" /> Try scanning
                    </button>
                  )}
                </div>
              </div>
              {i < steps.length - 1 && <ArrowRight className={cn("h-3 w-3 shrink-0 mt-3.5 transition-colors", done ? "text-emerald-300" : "text-slate-300")} />}
            </div>
          );
        })}
      </div>

      {/* Lens Demo Dialog */}
      <LensWorkflowDemo open={lensOpen} onOpenChange={setLensOpen} context={lensContext} />
    </div>
  );
};

export default WorkflowTracker;
